import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCheck,
  FaCheckDouble,
  FaCopy,
  FaExternalLinkAlt,
  FaKey,
  FaLink,
  FaLock,
  FaMinusCircle,
  FaPaperPlane,
  FaPaperclip,
  FaRedo,
  FaSave,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import { Drawer } from "../../../work/WorkUi";
import { useNotify } from "../../../../components/ConfirmModal";
import { SETUP_FILE_ACCEPT, formatBytes, uploadSetupFile } from "../../../../utils/directUpload";
import { errorMessage } from "../../dmApi";
import { NOT_APPLICABLE, OpenLink, SetupStatus, copyText, fmtWhen, isDone, ownerLabel } from "./setupUi";
import { SocialLinkCard } from "./SetupSocial";

const TEXT_TYPES = { url: "url", email: "email" };

function formFrom(step) {
  return {
    data: { ...(step.Data || {}) },
    secrets: {},
    notes: step.Notes || "",
    owner: step.Owner,
    assigned: step.Assigned_To ? String(step.Assigned_To) : "",
    due: step.Due_Date || "",
    status: step.Status,
    blockedReason: step.Blocked_Reason || "",
  };
}

function FieldInput({ field, form, setForm, disabled, secretSaved, linked }) {
  const id = `dms-f-${field.key}`;
  const value = form.data[field.key];
  const setData = (v) => setForm((f) => ({ ...f, data: { ...f.data, [field.key]: v } }));
  const label = (
    <>
      {field.label}
      {field.required ? <span className="dms-req" aria-label="required"> *</span> : null}
      {linked && field.type !== "secret" ? (
        <span className="dms-vault-hint dms-link-hint" title="Saved on the linked Social Connection">
          <FaLink aria-hidden="true" /> Social Connection
        </span>
      ) : null}
    </>
  );
  const secretPlace = linked ? "Social Connection" : "Vault";

  if (field.type === "checkbox") {
    return (
      <label className="dms-check" htmlFor={id}>
        <input id={id} type="checkbox" checked={value === true} disabled={disabled} onChange={(e) => setData(e.target.checked)} />
        <span>{label}</span>
      </label>
    );
  }
  if (field.type === "secret") {
    return (
      <label className="dmp-field" htmlFor={id}>
        <span>
          {label}
          <span className="dms-vault-hint">
            <FaLock aria-hidden="true" /> {secretSaved ? `saved in ${secretPlace}` : `goes to ${secretPlace}`}
          </span>
        </span>
        <input
          id={id}
          type="password"
          autoComplete="new-password"
          value={form.secrets[field.key] || ""}
          disabled={disabled}
          placeholder={secretSaved ? "•••••••• (type to replace)" : field.placeholder || ""}
          onChange={(e) => setForm((f) => ({ ...f, secrets: { ...f.secrets, [field.key]: e.target.value } }))}
        />
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <label className="dmp-field" htmlFor={id}>
        <span>{label}</span>
        <select id={id} value={value || ""} disabled={disabled} onChange={(e) => setData(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className="dmp-field dmp-field--full" htmlFor={id}>
        <span>{label}</span>
        <textarea id={id} rows={3} value={value || ""} disabled={disabled} placeholder={field.placeholder || ""} onChange={(e) => setData(e.target.value)} />
      </label>
    );
  }
  const link = field.type === "url" && /^https?:\/\/\S+\.\S+/i.test(String(value || "")) ? value : null;
  return (
    <label className="dmp-field" htmlFor={id}>
      <span>
        {label}
        {link ? <OpenLink href={link} className="dms-open--inline" title={`Open ${field.label} in a new window`} /> : null}
      </span>
      <input
        id={id}
        type={TEXT_TYPES[field.type] || "text"}
        className={field.type === "code" ? "dms-code-input" : undefined}
        value={value || ""}
        disabled={disabled}
        placeholder={field.placeholder || ""}
        onChange={(e) => setData(e.target.value)}
      />
    </label>
  );
}

function SetupStepDrawer({ api, stepId, tenantId = null, users = [], onClose, onChanged }) {
  const notify = useNotify();
  const isStaff = Boolean(api.isStaff);
  const [step, setStep] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [comment, setComment] = useState("");
  const [upload, setUpload] = useState(null);
  const fileInput = useRef(null);
  const uploadField = useRef(null);

  const load = useCallback(() => {
    setError("");
    return api
      .getSetupStep(stepId)
      .then((s) => {
        setStep(s);
        setForm(formFrom(s));
      })
      .catch((err) => setError(errorMessage(err, "Could not load this step")));
  }, [api, stepId]);

  useEffect(() => {
    setStep(null);
    load();
  }, [load]);

  const applyResult = (s) => {
    setStep(s);
    setForm(formFrom(s));
    onChanged?.();
  };

  const run = async (key, fn, success) => {
    setBusy(key);
    setError("");
    try {
      const result = await fn();
      if (result && result.Tenant_Step_Id) applyResult(result);
      if (success) notify.success(typeof success === "function" ? success(result) : success);
      return result;
    } catch (err) {
      setError(errorMessage(err, "Could not save"));
      return null;
    } finally {
      setBusy("");
    }
  };

  if (!step || !form) {
    return (
      <Drawer title="Setup step" onClose={onClose}>
        {error ? <div className="dmp-error">{error}</div> : <p className="dmp-muted">Loading…</p>}
      </Drawer>
    );
  }

  const canEdit = Boolean(step.Can_Edit);
  const fields = (step.Fields || []).filter((f) => f.type !== "file");
  const fileFields = (step.Fields || []).filter((f) => f.type === "file");
  const fieldLabel = (key) => (step.Fields || []).find((f) => f.key === key)?.label;
  const linkedKeys = new Set(step.Linked_Fields || []);

  const body = (extra = {}) => {
    const out = { Notes: form.notes };
    if (canEdit) {
      out.Data = form.data;
      const secrets = Object.fromEntries(Object.entries(form.secrets).filter(([, v]) => v));
      if (Object.keys(secrets).length) out.Secrets = secrets;
    }
    if (isStaff) {
      out.Owner = form.owner;
      out.Assigned_To = form.assigned ? Number(form.assigned) : null;
      out.Due_Date = form.due || null;
      out.Blocked_Reason = form.blockedReason || null;
      if (form.status !== step.Status) out.Status = form.status;
    }
    return { ...out, ...extra };
  };

  const save = (extra, message) => run(extra?.Status || "save", () => api.updateSetupStep(step.Tenant_Step_Id, body(extra)), message || "Saved");

  const verifyNow = () =>
    run("verify", () => api.verifySetupStep(step.Tenant_Step_Id), (s) => s?.Last_Check_Result || "Check finished");

  const postComment = async () => {
    const text = comment.trim();
    if (!text) return;
    const result = await run("comment", () => api.addSetupComment(step.Tenant_Step_Id, { Text: text }));
    if (result) {
      setComment("");
      setStep((s) => ({ ...s, Activity: result }));
    }
  };

  const pickFile = (field) => {
    uploadField.current = field;
    fileInput.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUpload({ name: file.name, pct: 0 });
    setError("");
    try {
      await uploadSetupFile(api, step.Tenant_Step_Id, file, {
        field: uploadField.current,
        onProgress: (p) => setUpload({ name: file.name, pct: Math.round(p * 100) }),
      });
      notify.success(`${file.name} uploaded`);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, err?.message || "Upload failed"));
    } finally {
      setUpload(null);
    }
  };

  const removeFile = async (f) => {
    setBusy(`file-${f.id}`);
    try {
      await api.deleteSetupFile(step.Tenant_Step_Id, f.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, "Could not remove the file"));
    } finally {
      setBusy("");
    }
  };

  /** ``value``: account id, "new" (create from the form's values) or null (unlink). */
  const linkSocial = (value) =>
    run(
      "link",
      () => api.updateSetupStep(step.Tenant_Step_Id, value === "new" ? { Social_Account_Id: value, Data: form.data } : { Social_Account_Id: value }),
      value === null ? "Unlinked; the values are back on this step" : "Linked to the Social Connection"
    );

  const applySocialValues = (values) => {
    const blank = Object.entries(values).filter(([key]) => !String(form.data[key] ?? "").trim());
    if (!blank.length) {
      notify.info("Those fields already have values.");
      return;
    }
    setForm((f) => ({ ...f, data: { ...f.data, ...Object.fromEntries(blank) } }));
    notify.success(`Filled ${blank.length} field${blank.length === 1 ? "" : "s"}. Save to keep them.`);
  };

  const copySnippet = async () => {
    if (await copyText(step.Snippet)) notify.success("Snippet copied");
  };

  const done = isDone(step.Status);
  const na = step.Status === NOT_APPLICABLE;
  // Clients follow Cloud Office's own steps by status and comments only.
  const statusOnly = !isStaff && step.Owner === "Agency";

  return (
    <Drawer title={step.Title} onClose={onClose}>
      <div className="dms-drawer">
        <div className="dms-drawer__top">
          <SetupStatus status={step.Status} />
          <span className={`dms-owner dms-owner--${step.Owner.toLowerCase()}`}>Owner: {ownerLabel(step.Owner)}</span>
          {step.Is_Optional ? <span className="dms-tag">optional</span> : null}
          {step.Applies_To !== "Any" ? <span className="dms-tag">{step.Applies_To}</span> : null}
        </div>

        {step.Description ? <p className="dms-drawer__desc">{step.Description}</p> : null}
        {step.Open_Url && !statusOnly ? (
          <div className="dms-drawer__open">
            <OpenLink href={step.Open_Url} label="Open account" className="dmp-btn dmp-btn--sm" />
            <span className="dmp-muted">Opens in a new window; sign in with the brand account if asked.</span>
          </div>
        ) : null}
        {error ? <div className="dmp-error">{error}</div> : null}

        {step.Client_Notes ? (
          <div className={`dms-expect${step.Owner === "Client" ? " is-client" : ""}`}>
            <h4 className="dms-expect__title">{isStaff ? "What we need from the client" : "What we need from you"}</h4>
            <div className="dms-expect__text">{step.Client_Notes}</div>
          </div>
        ) : null}

        {step.Waiting_For?.length && !done && !na ? (
          <div className="dmp-notice">Do this after: {step.Waiting_For.join(", ")}.</div>
        ) : null}
        {step.Status === "Blocked" && step.Blocked_Reason ? (
          <div className="dmp-error">Blocked: {step.Blocked_Reason}</div>
        ) : null}
        {!canEdit && !isStaff ? (
          <div className="dms-info">
            {step.Owner === "Agency"
              ? "Cloud Office is handling this step. You can follow its progress and add comments below."
              : "This step is closed. Ask Tumma in a comment if something needs to change."}
          </div>
        ) : null}

        {(step.Instructions || step.Help_Url) && !statusOnly ? (
          <div className="dms-instr">
            {step.Instructions ? <div className="dms-instr__text">{step.Instructions}</div> : null}
            {step.Help_Url ? (
              <a className="dmp-btn dmp-btn--ghost dmp-btn--sm dms-instr__link" href={step.Help_Url} target="_blank" rel="noreferrer">
                {step.Help_Label || "Open"} <FaExternalLinkAlt aria-hidden="true" />
              </a>
            ) : null}
          </div>
        ) : null}

        {step.Last_Check_Result && !statusOnly ? (
          <div className={`dms-check-result${step.Last_Check_Ok ? " is-ok" : step.Last_Check_Ok === false ? " is-bad" : ""}`}>
            <strong>Last check {fmtWhen(step.Last_Check_On)}:</strong> {step.Last_Check_Result}
          </div>
        ) : null}

        {statusOnly ? null : (
          <SocialLinkCard
            step={step}
            isStaff={isStaff}
            tenantId={tenantId}
            canEdit={canEdit}
            busy={!!busy}
            fieldLabel={fieldLabel}
            onLink={linkSocial}
            onUse={applySocialValues}
          />
        )}

        {fields.length && !statusOnly ? (
          <>
            <h4 className="dms-drawer__h">Details</h4>
            <div className="dmp-form-grid dms-fields">
              {fields.map((f) => (
                <FieldInput
                  key={f.key}
                  field={f}
                  form={form}
                  setForm={setForm}
                  disabled={!canEdit}
                  secretSaved={(step.Secret_Keys || []).includes(f.key)}
                  linked={linkedKeys.has(f.key)}
                />
              ))}
            </div>
            {fields.some((f) => f.type === "secret") ? (
              <p className="dmp-muted dms-vault-note">
                <FaKey aria-hidden="true" />{" "}
                {fields.some((f) => f.type === "secret" && !linkedKeys.has(f.key))
                  ? "Passwords and codes are stored encrypted in the client's Vault and are never shown here again."
                  : "The password is stored encrypted on the linked Social Account and is never shown here again."}
              </p>
            ) : null}
          </>
        ) : null}

        {step.Snippet && !statusOnly ? (
          <>
            <h4 className="dms-drawer__h">Install snippet</h4>
            {step.Snippet_Missing?.length ? (
              <p className="dmp-notice">
                Fill in {step.Snippet_Missing.map((k) => k.replace(/_/g, " ")).join(", ")} (in this or an earlier step) to complete the snippet.
              </p>
            ) : null}
            <div className="dms-snippet">
              <button type="button" className="dms-snippet__copy" onClick={copySnippet}>
                <FaCopy aria-hidden="true" /> Copy
              </button>
              <pre>{step.Snippet}</pre>
            </div>
          </>
        ) : null}

        {statusOnly && !(step.Files || []).length ? null : (
        <>
        <h4 className="dms-drawer__h">Files</h4>
        <div className="dms-files">
          {(step.Files || []).length === 0 ? <p className="dmp-muted">No files yet.</p> : null}
          {(step.Files || []).map((f) => (
            <div key={f.id} className="dms-file">
              <FaPaperclip aria-hidden="true" />
              {f.Url ? (
                <a href={f.Url} target="_blank" rel="noreferrer">
                  {f.name}
                </a>
              ) : (
                <span>{f.name}</span>
              )}
              {f.field ? <span className="dms-tag">{fieldLabel(f.field) || f.field}</span> : null}
              <span className="dmp-muted">{formatBytes(f.size)}</span>
              {canEdit ? (
                <button
                  type="button"
                  className="dms-icon-btn"
                  aria-label={`Remove ${f.name}`}
                  disabled={busy === `file-${f.id}`}
                  onClick={() => removeFile(f)}
                >
                  <FaTrash aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ))}
          {upload ? (
            <div className="dms-file is-uploading">
              <span>{upload.name}</span>
              <span className="dms-bar">
                <span style={{ width: `${upload.pct}%` }} />
              </span>
              <span className="dmp-muted">{upload.pct}%</span>
            </div>
          ) : null}
          {canEdit ? (
            <div className="dmp-actions">
              {fileFields.map((f) => (
                <button key={f.key} type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" disabled={!!upload} onClick={() => pickFile(f.key)}>
                  <FaPaperclip aria-hidden="true" /> {f.label}
                  {f.required ? " *" : ""}
                </button>
              ))}
              <button type="button" className="dmp-btn dmp-btn--ghost dmp-btn--sm" disabled={!!upload} onClick={() => pickFile(null)}>
                <FaPaperclip aria-hidden="true" /> {fileFields.length ? "Other file" : "Upload file"}
              </button>
            </div>
          ) : null}
          <input ref={fileInput} type="file" hidden accept={SETUP_FILE_ACCEPT} onChange={onFile} />
        </div>
        </>
        )}

        {isStaff ? (
          <>
            <h4 className="dms-drawer__h">Assignment</h4>
            <div className="dmp-form-grid">
              <label className="dmp-field">
                <span>Owner</span>
                <select value={form.owner} onChange={(e) => setForm((f) => ({ ...f, owner: e.target.value }))}>
                  <option value="Agency">Cloud Office</option>
                  <option value="Client">Client</option>
                </select>
              </label>
              <label className="dmp-field">
                <span>Assigned to</span>
                <select value={form.assigned} onChange={(e) => setForm((f) => ({ ...f, assigned: e.target.value }))}>
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.User_Id} value={u.User_Id}>
                      {u.Name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="dmp-field">
                <span>Due date</span>
                <input type="date" value={form.due} onChange={(e) => setForm((f) => ({ ...f, due: e.target.value }))} />
              </label>
              <label className="dmp-field">
                <span>Status</span>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  {[
                    "Not Started",
                    "In Progress",
                    "Waiting on Client",
                    "Submitted",
                    "Done",
                    "Verified",
                    "Blocked",
                    "Not Applicable",
                  ].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              {form.status === "Blocked" ? (
                <label className="dmp-field dmp-field--full">
                  <span>Blocked reason</span>
                  <input
                    value={form.blockedReason}
                    onChange={(e) => setForm((f) => ({ ...f, blockedReason: e.target.value }))}
                    placeholder="What is holding this up?"
                  />
                </label>
              ) : null}
            </div>
          </>
        ) : null}

        {statusOnly ? (
          form.notes ? (
            <>
              <h4 className="dms-drawer__h">Notes</h4>
              <p className="dms-drawer__desc">{form.notes}</p>
            </>
          ) : null
        ) : (
          <>
            <h4 className="dms-drawer__h">Notes</h4>
            <label className="dmp-field dmp-field--full">
              <span className="dms-sr">Notes</span>
              <textarea
                rows={2}
                value={form.notes}
                disabled={!isStaff && !canEdit}
                placeholder="Notes for the team and the client"
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </label>
          </>
        )}

        <div className="dmp-form-actions dms-drawer__actions">
          {isStaff || canEdit ? (
            <button type="button" className="dmp-btn" disabled={!!busy} onClick={() => save()}>
              <FaSave aria-hidden="true" /> {isStaff ? "Save" : "Save draft"}
            </button>
          ) : null}
          {!isStaff && canEdit ? (
            <button
              type="button"
              className="dmp-btn"
              disabled={!!busy}
              onClick={() => save({ Status: "Submitted" }, "Submitted to Tumma for review")}
            >
              <FaPaperPlane aria-hidden="true" /> Submit for review
            </button>
          ) : null}
          {isStaff && !done && !na ? (
            <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={() => save({ Status: "Done" }, "Marked done")}>
              <FaCheck aria-hidden="true" /> Mark done
            </button>
          ) : null}
          {isStaff && step.Status !== "Verified" && !na ? (
            step.Verify_Type && step.Verify_Type !== "manual" ? (
              <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={verifyNow}>
                <FaSyncAlt aria-hidden="true" className={busy === "verify" ? "dms-spin" : ""} /> Verify now
              </button>
            ) : (
              <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={() => save({ Status: "Verified" }, "Marked verified")}>
                <FaCheckDouble aria-hidden="true" /> Mark verified
              </button>
            )
          ) : null}
          {isStaff && step.Status === "Verified" && step.Verify_Type && step.Verify_Type !== "manual" ? (
            <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={verifyNow}>
              <FaSyncAlt aria-hidden="true" /> Re-check
            </button>
          ) : null}
          {isStaff && !na ? (
            <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={() => save({ Status: NOT_APPLICABLE }, "Marked not applicable")}>
              <FaMinusCircle aria-hidden="true" /> Not applicable
            </button>
          ) : null}
          {isStaff && (done || na) ? (
            <button type="button" className="dmp-btn dmp-btn--ghost" disabled={!!busy} onClick={() => save({ Status: "In Progress" }, "Reopened")}>
              <FaRedo aria-hidden="true" /> Reopen
            </button>
          ) : null}
        </div>

        <h4 className="dms-drawer__h">Activity</h4>
        <div className="dms-comment">
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" aria-label="Comment" />
          <button type="button" className="dmp-btn dmp-btn--sm" disabled={!comment.trim() || !!busy} onClick={postComment}>
            Comment
          </button>
        </div>
        <ul className="dms-log">
          {(step.Activity || []).map((a) => (
            <li key={a.Activity_Id} className={a.Action === "comment" ? "is-comment" : ""}>
              <span className="dms-log__when">{fmtWhen(a.Created_On)}</span>
              <span className="dms-log__who">
                {a.Actor_Name || (a.Actor_Kind === "client" ? "Client" : "Tumma")}
                {a.Actor_Kind === "client" ? <span className="dms-tag">client</span> : null}
              </span>
              <span className="dms-log__what">{a.Detail || a.Action}</span>
            </li>
          ))}
          {(step.Activity || []).length === 0 ? <li className="dmp-muted">No activity yet.</li> : null}
        </ul>
      </div>
    </Drawer>
  );
}

export default SetupStepDrawer;
